import { useCallback } from 'react';
import { useCopyText } from '@frameio/vapor-migration';
import { dispatchCopyLinkEvent } from '../../components/CopyLink';
import { useTrackUnsafe } from '../../segment/hooks';
import TEXT from './generated/TEXT/useCopyShareLink';

/** Copy-link event group shared by every Share copy-link surface. */
export const SHARE_LINK_GROUP_ID = 'share';

export type CopyLinkSourceForAnalytics = {
  actionSource?:
    | 'AllSharesListContextMenu'
    | 'AllSharesListTableCell'
    | 'AllUploadLinksListTableCell'
    | 'BreadcrumbsContextMenu'
    | 'CreationToast'
    | 'IntegrationsShareLink'
    | 'ProjectNavContextMenu'
    | 'QuickShare'
    | 'SettingsPanelButton'
    | 'SettingsPanelShareVisibility'
    | 'ShareContextMenu'
    | 'QuickshareCopyLinkButton'
    | 'QuickshareLinkAccess'
    | 'IntegrationsSequenceContextMenu'
    | 'AIChat';
};

type UseCopyShareLinkArgs = {
  shareId: string;
  shareUrl: string;
  skipAnalytics?: boolean;
} & CopyLinkSourceForAnalytics;

TEXT.DEFINE({
  /** Toast message shown when Share link is copied to clipboard */
  labelLinkCopied: 'Link copied to clipboard',
});

export function dispatchCopyShareLinkEvent(shareUrl: string) {
  dispatchCopyLinkEvent(SHARE_LINK_GROUP_ID, shareUrl);
}

/**
 * A hook that is responsible for copying the short Share URL, displaying a toast and
 * firing an analytics event along with it.
 *
 * @returns `copyShareLink` callback that takes `shareId` and an event origin string
 */
export const useCopyShareLink = (
  options: { displaySuccessToast: boolean } = { displaySuccessToast: true }
) => {
  const track = useTrackUnsafe();

  const { copyText } = useCopyText({
    displaySuccessToast: options.displaySuccessToast,
    toastTimeout: 3000,
  });

  // Extract TEXT properties to avoid calling hooks inside useCallback
  const textLabelLinkCopied = TEXT.labelLinkCopied;

  const copyShareLink = useCallback(
    async ({
      actionSource,
      shareId,
      shareUrl,
      skipAnalytics = false,
    }: UseCopyShareLinkArgs) => {
      await copyText(shareUrl, textLabelLinkCopied);
      dispatchCopyShareLinkEvent(shareUrl);
      if (!skipAnalytics) {
        track('share-link-copied', {
          share_id: shareId,
          source: actionSource,
        });
      }
    },
    [copyText, track, textLabelLinkCopied]
  );

  return copyShareLink;
};
